import React, { useEffect, useState } from "react";
import {
  Sun, Cloud, CloudSun, CloudRain, CloudSnow, CloudFog, CloudLightning, Wind, Loader2, Droplets,
} from "lucide-react";
import { TRIP_TARGET, fmtShipDate } from "../lib/api";

const LAT = 36.7466; // Göcek
const LON = 28.9389;
const TEN_DAYS_MS = 10 * 24 * 60 * 60 * 1000;
const TRIP_START = "2026-08-22"; // user-spec: trip days
const FORECAST_START = "2026-08-20";
const FORECAST_END = "2026-08-30";

function pickIcon(code) {
  // WMO weather codes
  if (code === 0) return Sun;
  if (code === 1 || code === 2) return CloudSun;
  if (code === 3) return Cloud;
  if (code === 45 || code === 48) return CloudFog;
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return CloudRain;
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return CloudSnow;
  if (code === 95 || code === 96 || code === 99) return CloudLightning;
  return Cloud;
}

function describe(code) {
  const m = {
    0: "Açık", 1: "Az bulutlu", 2: "Parçalı bulutlu", 3: "Bulutlu",
    45: "Sis", 48: "Kırağı sisi",
    51: "Hafif çisenti", 53: "Çisenti", 55: "Yoğun çisenti",
    61: "Hafif yağmur", 63: "Yağmur", 65: "Şiddetli yağmur",
    71: "Hafif kar", 73: "Kar", 75: "Yoğun kar",
    80: "Yağmur sağanak", 81: "Sağanak", 82: "Kuvvetli sağanak",
    95: "Gök gürültüsü", 96: "Gök gürültülü dolu", 99: "Şiddetli fırtına",
  };
  return m[code] || "—";
}

export default function WeatherCard() {
  const [state, setState] = useState({ loading: true, err: null, mode: "current", data: null });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const now = Date.now();
      const within10 = TRIP_TARGET.getTime() - now <= TEN_DAYS_MS;
      try {
        if (within10) {
          const url = `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}&daily=weather_code,temperature_2m_max,temperature_2m_min,wind_speed_10m_max&start_date=${FORECAST_START}&end_date=${FORECAST_END}&timezone=Europe%2FIstanbul`;
          const r = await fetch(url);
          const j = await r.json();
          if (!cancelled) setState({ loading: false, err: null, mode: "10day", data: j });
        } else {
          const url = `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}&current=temperature_2m,weather_code,wind_speed_10m,relative_humidity_2m&timezone=Europe%2FIstanbul`;
          const r = await fetch(url);
          const j = await r.json();
          if (!cancelled) setState({ loading: false, err: null, mode: "current", data: j });
        }
      } catch (e) {
        if (!cancelled) setState({ loading: false, err: "Hava bilgisi alınamadı", mode: "current", data: null });
      }
    };
    load();
    const id = setInterval(load, 30 * 60 * 1000); // refresh every 30 min
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const inner = (() => {
    if (state.loading) return (
      <div className="flex items-center gap-2 text-sm" style={{ color: "hsl(var(--text-2))" }}>
        <Loader2 className="w-4 h-4 animate-spin" /> Hava yükleniyor...
      </div>
    );
    if (state.err || !state.data) return (
      <div className="text-xs" style={{ color: "hsl(var(--text-2))" }}>Hava bilgisi alınamadı</div>
    );
    if (state.mode === "current") {
      const cur = state.data.current || {};
      const Icon = pickIcon(cur.weather_code);
      return (
        <div className="flex flex-col h-full justify-between">
          <div className="flex items-center justify-between">
            <Icon className="w-7 h-7" style={{ color: "#4992ff" }} />
            <div className="text-2xl md:text-3xl font-display font-semibold" data-testid="weather-temp">
              {Math.round(cur.temperature_2m ?? 0)}°
            </div>
          </div>
          <div className="text-[11px] truncate" style={{ color: "hsl(var(--text-2))" }} data-testid="weather-desc">
            {describe(cur.weather_code)}
          </div>
          <div className="flex items-center gap-2 text-[10px]" style={{ color: "hsl(var(--text-2))" }}>
            <span className="flex items-center gap-1"><Wind className="w-3 h-3" />{Math.round(cur.wind_speed_10m ?? 0)} km/s</span>
            {cur.relative_humidity_2m != null && (
              <span className="flex items-center gap-1"><Droplets className="w-3 h-3" />%{cur.relative_humidity_2m}</span>
            )}
          </div>
        </div>
      );
    }
    // 10-day mode: pick the trip-start date for hero, list rest as mini ticks
    const d = state.data.daily || {};
    const dates = d.time || [];
    const codes = d.weather_code || [];
    const hi = d.temperature_2m_max || [];
    const lo = d.temperature_2m_min || [];
    const heroIdx = Math.max(0, dates.indexOf(TRIP_START));
    const Icon = pickIcon(codes[heroIdx]);
    return (
      <div className="flex flex-col h-full justify-between">
        <div className="flex items-center justify-between">
          <Icon className="w-7 h-7" style={{ color: "#4992ff" }} />
          <div className="text-right">
            <div className="text-xl font-display font-semibold tabular-nums" data-testid="weather-hi-lo">
              {Math.round(hi[heroIdx] ?? 0)}°<span className="text-xs" style={{ color: "hsl(var(--text-2))" }}>/{Math.round(lo[heroIdx] ?? 0)}°</span>
            </div>
          </div>
        </div>
        <div className="text-[10px]" style={{ color: "hsl(var(--text-2))" }}>22 Ağu — kalkış</div>
        <div className="flex gap-0.5 mt-1 overflow-hidden" data-testid="weather-strip">
          {dates.map((dt, i) => {
            const I = pickIcon(codes[i]);
            return (
              <div key={dt} className="flex-1 min-w-0 flex flex-col items-center" title={`${fmtShipDate(dt + " 12:00")} · ${Math.round(hi[i])}° / ${Math.round(lo[i])}°`}>
                <I className="w-2.5 h-2.5" style={{ color: "#4992ff" }} />
                <div className="text-[8px] tabular-nums" style={{ color: "hsl(var(--text-2))" }}>{Math.round(hi[i])}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  })();

  return (
    <div className="tk-card p-4 flex flex-col" data-testid="weather-card">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] md:text-xs uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--text-2))" }}>
          Göcek
        </div>
        <Cloud className="w-3.5 h-3.5" style={{ color: "hsl(var(--text-2))" }} />
      </div>
      <div className="flex-1">{inner}</div>
    </div>
  );
}
